import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './CoopInvitePopup.css';

// Popup สหกรณ์ออมทรัพย์ มี 3 โหมด (แสดงครั้งละโหมดเดียว เรียงตามลำดับความสำคัญ):
//
// โหมด loan — แจ้งข่าวฟีเจอร์ "ยอดกู้สหกรณ์" ให้ทุกคน (ที่ยังไม่ได้บันทึกยอดกู้) ทราบอย่างน้อย 1 รอบ
//   - กดปิด / ไว้ภายหลัง       -> เงียบถึงสิ้นเดือนนี้ แล้วแจ้งอีกครั้งต้นเดือนหน้า (จำในเครื่อง)
//   - "รับทราบ ไม่ต้องแจ้งอีก" -> บันทึกฝั่ง server (ทุกอุปกรณ์) ไม่แจ้งอีกเลย
//   - บอกว่าจดภายหลังได้ที่เมนู "สหกรณ์"
//
// โหมด invite — ผู้ที่ยังไม่เคยบันทึกยอดเลย (เตือนซ้ำทุกเดือน)
//   - กดปิด / ไว้ภายหลัง   -> เงียบถึงสิ้นเดือนนี้ แล้วเด้งใหม่ตั้งแต่วันที่ 1 ของเดือนถัดไป
//   - "ห้ามแสดงอีกตลอดไป"  -> บันทึกฝั่ง server (ทุกอุปกรณ์) ไม่เด้งอีกเลย
//
// โหมด announce — ผู้ที่บันทึกยอดหุ้นไว้แล้ว แต่ยังไม่มีรายการเงินฝากออมทรัพย์พิเศษ
//   - กดปิด/รับทราบ = ไม่แจ้งซ้ำอีก (จำในเครื่อง)
const MONTH_KEY = 'imoney_coop_invite_closed_month';
const LOAN_MONTH_KEY = 'imoney_coop_loan_closed_month';
const SPECIAL_ANNOUNCE_KEY = 'imoney_coop_special_announced';
const SHOW_DELAY_MS = 1800; // หน่วงให้ popup ติดตั้ง PWA (ถ้ามี) แสดงก่อน ไม่ให้ซ้อนกันทันที

// 'YYYY-MM' ของเดือนปัจจุบัน — ใช้เทียบว่าปิดไปแล้ว "ในเดือนนี้" หรือยัง
// พอขึ้นวันที่ 1 ของเดือนใหม่ ค่าจะไม่ตรงกับที่เก็บไว้ popup จึงกลับมาเด้งเอง
function currentMonthKey() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function closedThisMonth(key) {
    try { return localStorage.getItem(key) === currentMonthKey(); } catch (e) { return false; }
}
function setClosedThisMonth(key) {
    try { localStorage.setItem(key, currentMonthKey()); } catch (e) {}
}

function specialAnnounced() {
    try { return localStorage.getItem(SPECIAL_ANNOUNCE_KEY) === '1'; } catch (e) { return false; }
}
function setSpecialAnnounced() {
    try { localStorage.setItem(SPECIAL_ANNOUNCE_KEY, '1'); } catch (e) {}
}

export default function CoopInvitePopup() {
    const navigate = useNavigate();
    const [mode, setMode] = useState(null); // null | 'loan' | 'invite' | 'announce'

    useEffect(() => {
        let cancelled = false;
        let timer = null;
        (async () => {
            try {
                const r = await api.get('/coop/me');
                if (cancelled) return;
                const entries = r.data.entries || [];
                const hasAny = entries.length > 0;
                const hasSpecial = entries.some(e => e.account_type === 'special');
                const hasLoan = entries.some(e => e.account_type === 'loan');

                let next = null;
                if (!hasLoan && !r.data.loanAnnounceDismissed && !closedThisMonth(LOAN_MONTH_KEY)) {
                    // ฟีเจอร์ใหม่ -> แจ้งทุกคน (รวมคนที่เคยปิด popup เชิญถาวร) จนกว่าจะกดไม่ต้องแจ้งอีก
                    next = 'loan';
                } else if (!hasAny) {
                    // ยังไม่เคยบันทึก -> ชวนใช้งาน (เตือนซ้ำรายเดือน)
                    if (!r.data.popupDismissed && !closedThisMonth(MONTH_KEY)) next = 'invite';
                } else if (!hasSpecial && !specialAnnounced()) {
                    // บันทึกหุ้นแล้ว -> แจ้งข่าวว่ารองรับเงินฝากออมทรัพย์พิเศษแล้ว (ครั้งเดียว)
                    next = 'announce';
                }
                if (!next) return;
                timer = setTimeout(() => { if (!cancelled) setMode(next); }, SHOW_DELAY_MS);
            } catch (e) {
                // เงียบไว้ — ไม่ให้ popup กวนถ้า API มีปัญหา
            }
        })();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, []);

    // ---- โหมด loan ----
    const loanLater = () => {
        setClosedThisMonth(LOAN_MONTH_KEY);
        setMode(null);
    };
    const loanNever = async () => {
        setClosedThisMonth(LOAN_MONTH_KEY);
        setMode(null);
        try { await api.post('/coop/loan-announce-dismiss'); } catch (e) {}
    };
    const startLoan = () => {
        setClosedThisMonth(LOAN_MONTH_KEY);
        setMode(null);
        navigate('/coop?new=loan');
    };

    // ---- โหมด invite ----
    const closeForThisMonth = () => {
        setClosedThisMonth(MONTH_KEY);
        setMode(null);
    };
    const dismissForever = async () => {
        setClosedThisMonth(MONTH_KEY);
        setMode(null);
        try { await api.post('/coop/popup-dismiss'); } catch (e) {}
    };
    const startInvite = () => {
        setClosedThisMonth(MONTH_KEY);
        setMode(null);
        navigate('/coop');
    };

    // ---- โหมด announce ----
    const ackAnnounce = () => {
        setSpecialAnnounced();
        setMode(null);
    };
    const startSpecial = () => {
        setSpecialAnnounced();
        setMode(null);
        navigate('/coop?new=special');
    };

    if (!mode) return null;

    const onClose = mode === 'loan' ? loanLater : mode === 'announce' ? ackAnnounce : closeForThisMonth;
    const iconClass = mode === 'loan' ? 'coop-pop-icon-loan' : mode === 'announce' ? 'coop-pop-icon-gold' : '';

    return (
        <div className="coop-pop-backdrop" role="dialog" aria-modal="true" aria-labelledby="coop-pop-title">
            <div className="coop-pop" onClick={(e) => e.stopPropagation()}>
                <button type="button" className="coop-pop-x" onClick={onClose} aria-label="ปิด">×</button>

                <div className={`coop-pop-icon ${iconClass}`} aria-hidden="true">
                    {mode === 'loan' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <path d="M14 2v6h6" />
                            <path d="M8 13l3 3 5-6" />
                        </svg>
                    ) : mode === 'announce' ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l2.4 4.86 5.36.78-3.88 3.78.92 5.34L12 14.24l-4.8 2.52.92-5.34-3.88-3.78 5.36-.78L12 2z" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 5H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" />
                            <path d="M16 12h5" />
                            <circle cx="16" cy="12" r="1.5" />
                        </svg>
                    )}
                </div>

                {mode === 'loan' && (
                    <>
                        <h3 id="coop-pop-title" className="coop-pop-title coop-pop-title-loan">ใหม่! ช่วยจำยอดกู้สหกรณ์ได้แล้ว</h3>
                        <p className="coop-pop-lead">
                            กรอก<strong> ยอดหนี้คงเหลือ</strong> ตามหนังสือยืนยันยอด และ<strong> ยอดที่ถูกหักชำระต่อเดือน</strong>
                            {' '}ระบบจะลดยอดหนี้ประมาณการให้ทุกเดือน พร้อมบอกว่าเหลืออีกกี่งวด
                        </p>
                        <p className="coop-pop-tip">
                            ยังไม่สะดวกตอนนี้? เข้าไปจดภายหลังได้ทุกเมื่อที่เมนู <strong>สหกรณ์</strong>
                        </p>

                        <button type="button" className="coop-pop-primary coop-pop-primary-loan" onClick={startLoan}>
                            บันทึกยอดกู้เลย
                        </button>

                        <button type="button" className="coop-pop-later" onClick={loanLater}>
                            ไว้ภายหลัง (เตือนอีกครั้งต้นเดือนหน้า)
                        </button>

                        <button type="button" className="coop-pop-never" onClick={loanNever}>
                            รับทราบ ไม่ต้องแจ้งอีก
                        </button>
                    </>
                )}

                {mode === 'announce' && (
                    <>
                        <h3 id="coop-pop-title" className="coop-pop-title">ใหม่! จดยอดเงินฝากออมทรัพย์พิเศษได้แล้ว</h3>
                        <p className="coop-pop-lead">
                            นอกจากยอดหุ้นสหกรณ์ที่คุณบันทึกไว้ ตอนนี้ระบบรองรับจดยอด
                            <strong> เงินฝากออมทรัพย์พิเศษ</strong> ด้วยแล้ว —
                            บัญชีที่ฝากเท่าไหร่ก็ได้และดอกเบี้ยสูง จดยอดจากหนังสือยืนยันไว้กันลืมได้เลย
                        </p>

                        <button type="button" className="coop-pop-primary" onClick={startSpecial}>
                            บันทึกยอดฝากพิเศษเลย
                        </button>

                        <button type="button" className="coop-pop-later" onClick={ackAnnounce}>
                            รับทราบ ไม่ต้องแจ้งอีก
                        </button>
                    </>
                )}

                {mode === 'invite' && (
                    <>
                        <h3 id="coop-pop-title" className="coop-pop-title">ช่วยจำยอดเงินสหกรณ์ออมทรัพย์</h3>
                        <p className="coop-pop-lead">
                            ระบบมีที่เก็บยอดเงินสหกรณ์ออมทรัพย์ รพ.ศก. ให้แล้ว — บันทึกได้ทั้ง
                            <strong> หุ้นสหกรณ์</strong>,<strong> เงินฝากออมทรัพย์พิเศษ</strong> และ<strong> ยอดกู้</strong> จาก "หนังสือยืนยันยอด"
                            ระบบจะช่วยจำให้ และถ้าระบุยอดหักรายเดือน ระบบจะคำนวณยอดประมาณการให้เองทุกเดือน
                        </p>

                        <button type="button" className="coop-pop-primary" onClick={startInvite}>
                            บันทึกยอดเลย
                        </button>

                        <button type="button" className="coop-pop-later" onClick={closeForThisMonth}>
                            ไว้ภายหลัง (เตือนอีกครั้งต้นเดือนหน้า)
                        </button>

                        <button type="button" className="coop-pop-never" onClick={dismissForever}>
                            ห้ามแสดงอีกตลอดไป
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
